import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import { fetchKitchenListAsync } from './kitchenListSlice';
import { Edit, Delete } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { AxiosError } from 'axios';
import Kitchen from '../../../app/data/models/kitchens/kitchen';
import FillWidthCell from '../../../app/components/FillWidthCell';
import StyledTableRow from '../../../app/components/StyledTableRow';
import { useTranslation } from 'react-i18next';
import ListTitleBox from '../../../app/components/list/ListTitleBox';
import LoadingComponent from '../../../app/layout/LoadingComponent';
import { toast } from 'react-toastify';
import { deleteKitchenAsync } from './kitchenListSlice';
import { ConfirmDialog } from '../../../app/components/ConfirmDialog';
import EmptyScreen from '../../../app/components/EmptyScreen';

const KitchenList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { restaurantId } = useParams<{
    restaurantId: string;
  }>();
  const { kitchens, status } = useAppSelector((state) => state.kitchens);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState<string>('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [kitchenToDelete, setKitchenToDelete] = useState<Kitchen | null>(null);

  useEffect(() => {
    dispatch(fetchKitchenListAsync(restaurantId));
  }, [restaurantId, dispatch]);

  const handleDeleteClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    kitchen: Kitchen
  ) => {
    event.stopPropagation();
    setKitchenToDelete(kitchen);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!kitchenToDelete) return;

    setLoading(true);
    setTarget(kitchenToDelete.id);
    try {
      const result = await dispatch(
        deleteKitchenAsync({ restaurantId, kitchenId: kitchenToDelete.id })
      ).unwrap();
      if (!result) {
        toast.error(t('kitchen.delete.error'));
      }
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.title || t('kitchen.delete.error'));
      } else if (error instanceof Error) {
        toast.error(error.message || t('kitchen.delete.error'));
      } else {
        toast.error(t('kitchen.delete.error'));
      }
    } finally {
      setLoading(false);
      setTarget('');
      setIsDeleteDialogOpen(false);
      setKitchenToDelete(null);
    }
  };

  const handleKitchenClick = (kitchen: Kitchen) => {
    navigate(`/manager/restaurants/${restaurantId}/kitchens/${kitchen.id}`);
  };

  const handleEditKitchen = useCallback(
    async (kitchen: Kitchen) => {
      navigate(
        `/manager/restaurants/${restaurantId}/kitchens/${kitchen.id}/edit`
      );
    },
    [navigate, restaurantId]
  );

  if (status.includes('pending')) return <LoadingComponent />;

  if (!kitchens || kitchens.length === 0)
    return (
      <EmptyScreen
        title={t('kitchen.list.title')}
        emptyText={t('kitchen.list.empty')}
        onPrimaryClick={() =>
          navigate(`/manager/restaurants/${restaurantId}/kitchens/create`)
        }
      />
    );

  return (
    <>
      <ConfirmDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('kitchen.delete.title')}
        message={t('kitchen.delete.message')}
        confirmText={t('common.delete')}
      />
      <ListTitleBox
        title={t('kitchen.list.title')}
        onPrimaryClick={() =>
          navigate(`/manager/restaurants/${restaurantId}/kitchens/create`)
        }
      />
      <TableContainer component={Paper} sx={{ width: '100%', mb: 2 }}>
        <Table aria-label={t('kitchen.list.title')}>
          <TableHead>
            <TableRow>
              <TableCell width="50px" align="left">
                #
              </TableCell>
              <FillWidthCell align="left">
                {t('kitchen.list.header.name')}
              </FillWidthCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {kitchens.map((kitchen, index) => (
              <StyledTableRow
                key={kitchen.id}
                onClick={() => handleKitchenClick(kitchen)}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  width: '100%',
                }}
              >
                <TableCell width="50px" align="left" component="th" scope="row">
                  {index + 1}
                </TableCell>
                <TableCell align="left">{kitchen.name}</TableCell>
                <TableCell align="right">
                  <IconButton
                    sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      handleEditKitchen(kitchen);
                    }}
                    size="small"
                  >
                    <Edit />
                  </IconButton>
                  <LoadingButton
                    sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
                    loading={loading && target === kitchen.id}
                    onClick={(e) => handleDeleteClick(e, kitchen)}
                    color="error"
                    size="small"
                  >
                    <Delete />
                  </LoadingButton>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default KitchenList;
